import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider, useI18n } from "@/i18n/I18n";
import { AuthProvider, useAuth } from "@/context/Auth";
import { AppShell } from "@/components/AppShell";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import JoinPage from "@/pages/JoinPage";
import Dashboard from "@/pages/Dashboard";
import Locations from "@/pages/Locations";
import LocationDetail from "@/pages/LocationDetail";
import Items from "@/pages/Items";
import ItemForm from "@/pages/ItemForm";
import ItemDetail from "@/pages/ItemDetail";
import SearchPage from "@/pages/SearchPage";
import ScanPage from "@/pages/ScanPage";
import Categories from "@/pages/Categories";
import Members from "@/pages/Members";
import Plans from "@/pages/Plans";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import ResetPassword from "@/pages/ResetPassword";
import PaymentResult from "@/pages/PaymentResult";
import LabelsPage from "@/pages/LabelsPage";
import MovePage from "@/pages/MovePage";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="app-loader">
    <img src="/icons/icon-192.png" alt="" className="w-12 h-12 rounded-xl animate-pulse" />
  </div>
);

function Protected({ children, needWs = true, admin = false }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loader />;
  if (!me) {
    if (location.pathname.startsWith("/l/")) sessionStorage.setItem("rangeo_after_login", location.pathname);
    return <Navigate to="/login" replace />;
  }
  if (admin && !me.user.is_superadmin) return <Navigate to="/" replace />;
  if (needWs && !me.workspace) return <Navigate to={me.user.is_superadmin ? "/admin" : "/onboarding"} replace />;
  return <AppShell>{children}</AppShell>;
}

const LocRedirect = () => <Navigate to={`/locations/${useParams().id}`} replace />;

const Home = () => {
  const next = sessionStorage.getItem("rangeo_after_login");
  if (next) { sessionStorage.removeItem("rangeo_after_login"); return <Navigate to={next} replace />; }
  return <Dashboard />;
};

const P = (el, opts) => <Protected {...opts}>{el}</Protected>;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<AuthPage />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/payment/success" element={P(<PaymentResult />)} />
      <Route path="/payment/cancel" element={P(<PaymentResult />)} />
      <Route path="/locations/:id/labels" element={P(<LabelsPage />)} />
      <Route path="/join/:code" element={<JoinPage />} />
      <Route path="/onboarding" element={<OnboardingGate />} />
      <Route path="/" element={P(<Home />)} />
      <Route path="/l/:id" element={P(<LocRedirect />)} />
      <Route path="/locations" element={P(<Locations />)} />
      <Route path="/locations/:id" element={P(<LocationDetail />)} />
      <Route path="/items" element={P(<Items />)} />
      <Route path="/items/new" element={P(<ItemForm />)} />
      <Route path="/items/:id" element={P(<ItemDetail />)} />
      <Route path="/items/:id/edit" element={P(<ItemForm key="edit" />)} />
      <Route path="/search" element={P(<SearchPage />)} />
      <Route path="/scan" element={P(<ScanPage />)} />
      <Route path="/categories" element={P(<Categories />)} />
      <Route path="/move" element={P(<MovePage />)} />
      <Route path="/members" element={P(<Members />)} />
      <Route path="/plans" element={P(<Plans />)} />
      <Route path="/settings" element={P(<Settings />)} />
      <Route path="/admin" element={P(<Admin />, { needWs: false, admin: true })} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function OnboardingGate() {
  const { me, loading } = useAuth();
  if (loading) return <Loader />;
  if (!me) return <Navigate to="/login" replace />;
  return <Onboarding />;
}

const ThemedToaster = () => {
  const { dir } = useI18n();
  return <Toaster richColors position={dir === "rtl" ? "top-left" : "top-right"} />;
};

export default function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <ThemedToaster />
        </AuthProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}
